// components/Modals/XBulkModal/XBulkModal.js
import React, { useState, useRef, useEffect } from 'react';
import './XBulkModal.css';
import { useConfirmModal } from '../../../contexts/ConfirmModalContext';

const XBulkModal = ({ 
  isOpen, 
  onClose, 
  accounts,
  mode, // 'tweet' veya 'retweet'
  showMessage, 
  API_URL 
}) => {
  // Context hook
  const { showConfirm } = useConfirmModal();
  
  // Başlangıç form state'i (değişiklik kontrolü için)
  const initialFormRef = useRef(null);

  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [tweetText, setTweetText] = useState('');
  const [tweetUrl, setTweetUrl] = useState('');
  const [tweetMedia, setTweetMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  
  const fileInputRef = useRef(null);

  // Modal açıldığında başlangıç state'ini kaydet
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        initialFormRef.current = JSON.stringify({
          selectedAccounts: [],
          tweetText: '',
          tweetUrl: '',
          hasMedia: false
        });
      }, 100);
    } else {
      initialFormRef.current = null;
    }
  }, [isOpen]);

  // Form değişti mi kontrol et
  const hasFormChanged = () => {
    if (!initialFormRef.current) return false;
    
    const current = JSON.stringify({
      selectedAccounts: selectedAccounts,
      tweetText: tweetText,
      tweetUrl: tweetUrl,
      hasMedia: tweetMedia !== null
    });
    
    return current !== initialFormRef.current;
  };

  if (!isOpen) return null;

  const isTweetMode = mode === 'tweet';
  const title = isTweetMode ? 'Toplu Tweet Gönder' : 'Toplu Retweet Yap';

  // Hesap seçimi toggle
  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  // Tümünü seç/kaldır
  const toggleAll = () => {
    if (selectedAccounts.length === accounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(accounts.map(acc => acc.id));
    }
  };

  // Medya seç
  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview({
        file,
        preview: reader.result,
        type: file.type.startsWith('image/') ? 'image' : 'video'
      });
    };
    reader.readAsDataURL(file);
    setTweetMedia(file);
  };

  // Medya sil
  const removeMedia = () => {
    setTweetMedia(null);
    setMediaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Toplu Tweet gönder
  const sendBulkTweet = async () => {
    if (selectedAccounts.length === 0) {
      showMessage('En az bir hesap seçmelisiniz', 'error');
      return;
    }

    if (!tweetText.trim()) {
      showMessage('Tweet metni gerekli', 'error');
      return;
    }

    if (tweetText.length > 280) {
      showMessage('Tweet 280 karakteri aşamaz', 'error');
      return;
    }

    try {
      setSending(true);
      setResults(null);

      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('accountIds', JSON.stringify(selectedAccounts));
      formData.append('text', tweetText);
      
      if (tweetMedia) {
        formData.append('media', tweetMedia);
      }

      const response = await fetch(`${API_URL}/platforms/twitter/bulk-tweet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Toplu tweet gönderilemedi');
      }

      setResults(data.results);
      showMessage(data.message, 'success');

    } catch (error) {
      console.error('Toplu tweet hatası:', error);
      showMessage(error.message || 'Toplu tweet gönderilemedi', 'error');
    } finally {
      setSending(false);
    }
  };

  // Toplu Retweet yap
  const sendBulkRetweet = async () => {
    if (selectedAccounts.length === 0) {
      showMessage('En az bir hesap seçmelisiniz', 'error');
      return;
    }

    if (!tweetUrl.trim()) {
      showMessage('Tweet URL\'i gerekli', 'error');
      return;
    }

    if (!tweetUrl.match(/twitter\.com|x\.com/)) {
      showMessage('Geçersiz Twitter/X URL\'i', 'error');
      return;
    }

    try {
      setSending(true);
      setResults(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/platforms/twitter/bulk-retweet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountIds: selectedAccounts,
          tweetUrl: tweetUrl
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Toplu retweet yapılamadı');
      }

      setResults(data.results);
      showMessage(data.message, 'success');

    } catch (error) {
      console.error('Toplu retweet hatası:', error);
      showMessage(error.message || 'Toplu retweet yapılamadı', 'error');
    } finally {
      setSending(false);
    }
  };

  // State'leri temizle
  const clearAndClose = () => {
    setSelectedAccounts([]);
    setTweetText('');
    setTweetUrl('');
    setTweetMedia(null);
    setMediaPreview(null);
    setResults(null);
    onClose();
  };

  // Modal kapat (değişiklik kontrolü ile)
  const handleClose = () => {
    if (hasFormChanged()) {
      showConfirm(async () => {
        clearAndClose();
      });
    } else {
      clearAndClose();
    }
  };

  // Karakter sayısı
  const remainingChars = 280 - tweetText.length;

  return (
    <div className="x-bulk-modal-overlay" onClick={handleClose}>
      <div className="x-bulk-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="x-bulk-modal-header">
          <h3>{title}</h3>
          <button className="x-bulk-modal-close" onClick={handleClose}>✕</button>
        </div>

        {/* Body */}
        <div className="x-bulk-modal-body">
          {/* Hesap Seçimi */}
          <div className="x-bulk-section">
            <div className="x-bulk-section-header">
              <h4>Hesapları Seç ({selectedAccounts.length}/{accounts.length})</h4>
              <button 
                className="x-bulk-select-all-btn"
                onClick={toggleAll}
              >
                {selectedAccounts.length === accounts.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
              </button>
            </div>
            
            <div className="x-bulk-accounts-grid">
              {accounts.map(account => (
                <div 
                  key={account.id}
                  className={`x-bulk-account-item ${selectedAccounts.includes(account.id) ? 'selected' : ''}`}
                  onClick={() => toggleAccount(account.id)}
                >
                  <div className="x-bulk-account-checkbox">
                    {selectedAccounts.includes(account.id) && <span>✓</span>}
                  </div>
                  {account.profile_image_url && (
                    <img 
                      src={account.profile_image_url} 
                      alt={account.name}
                      className="x-bulk-account-avatar"
                    />
                  )}
                  <div className="x-bulk-account-info">
                    <span className="x-bulk-account-name">{account.name}</span>
                    <span className="x-bulk-account-username">@{account.username}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tweet Modu - Metin ve Medya */}
          {isTweetMode && (
            <div className="x-bulk-section">
              <h4>Tweet İçeriği</h4>
              <textarea
                className="x-bulk-textarea"
                placeholder="Ne düşünüyorsunuz?"
                value={tweetText}
                onChange={(e) => setTweetText(e.target.value)}
                maxLength={280}
                rows={4}
              />
              
              <div className="x-bulk-textarea-footer">
                <label className="x-bulk-media-btn">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleMediaSelect}
                    style={{ display: 'none' }}
                  />
                  📷 Medya Ekle
                </label>
                <span className={`x-bulk-char-count ${remainingChars < 20 ? 'warning' : ''} ${remainingChars < 0 ? 'error' : ''}`}>
                  {remainingChars}
                </span>
              </div>

              {mediaPreview && (
                <div className="x-bulk-media-preview">
                  {mediaPreview.type === 'image' ? (
                    <img src={mediaPreview.preview} alt="Preview" />
                  ) : (
                    <video src={mediaPreview.preview} controls />
                  )}
                  <button className="x-bulk-media-remove" onClick={removeMedia}>✕</button>
                </div>
              )}
            </div>
          )}

          {/* Retweet Modu - URL */}
          {!isTweetMode && (
            <div className="x-bulk-section">
              <h4>Tweet URL</h4>
              <input
                type="text"
                className="x-bulk-input"
                placeholder="https://twitter.com/user/status/123456789 veya https://x.com/user/status/123456789"
                value={tweetUrl}
                onChange={(e) => setTweetUrl(e.target.value)}
              />
              <p className="x-bulk-hint">
                Retweet yapmak istediğiniz tweet'in URL'ini yapıştırın
              </p>
            </div>
          )}

          {/* Sonuçlar */}
          {results && (
            <div className="x-bulk-section">
              <h4>Sonuçlar</h4>
              <div className="x-bulk-results">
                {results.map((result, index) => (
                  <div 
                    key={index} 
                    className={`x-bulk-result-item ${result.success ? 'success' : 'error'}`}
                  >
                    <span className="x-bulk-result-icon">
                      {result.success ? '✅' : '❌'}
                    </span>
                    <span className="x-bulk-result-username">
                      @{result.username || `Hesap #${result.accountId}`}
                    </span>
                    <span className="x-bulk-result-status">
                      {result.success 
                        ? (result.tweetUrl ? <a href={result.tweetUrl} target="_blank" rel="noopener noreferrer">Görüntüle</a> : 'Başarılı')
                        : result.error
                      }
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="x-bulk-modal-footer">
          <button className="x-bulk-cancel-btn" onClick={handleClose}>
            İptal
          </button>
          <button 
            className="x-bulk-send-btn"
            onClick={isTweetMode ? sendBulkTweet : sendBulkRetweet}
            disabled={sending || selectedAccounts.length === 0 || (isTweetMode ? !tweetText.trim() : !tweetUrl.trim())}
          >
            {sending ? (
              <>
                <span className="spinner-small"></span>
                Gönderiliyor...
              </>
            ) : (
              isTweetMode ? `${selectedAccounts.length} Hesaba Tweet Gönder` : `${selectedAccounts.length} Hesapla RT Yap`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default XBulkModal;